import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, BookOpen, LibraryBig, Mail, ShoppingBag, ExternalLink } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Reveal } from "@/hooks/useReveal";
import { BOOKS, EMAIL } from "@/content/site";
import { buildBooksJsonLd } from "@/lib/books-jsonld";
import { BookCoverImage, getBookPicture } from "@/components/site/BookCoverImage";
import { trackCTA } from "@/lib/tracking";


import promoFathersDay from "@/assets/books/promo-fathers-day-bundle.png";
import promoSignature from "@/assets/books/promo-signature-collection.png";
import promoTransformation from "@/assets/books/promo-transformation-collection.png";
import promoCheatCodeBundle from "@/assets/books/promo-cheat-code-ultimate-bundle.png";

const FORMATS = Array.from(new Set(BOOKS.map((book) => book.format)));

const STOREFRONTS = [
  {
    name: "Books.by / DrSullySpeaks",
    desc: "Primary storefront for ebooks, paperbacks, and direct-from-author titles.",
    href: "https://books.by/drsullyspeaks",
  },
  {
    name: "Lulu Spotlight / DrSullySpeaks",
    desc: "Premium print editions, journals, and exclusive collections.",
    href: "https://www.lulu.com/spotlight/drsullyspeaks",
  },
];

const PROMOS = [
  {
    image: promoCheatCodeBundle,
    title: "The Cheat Code: Ultimate Bundle",
    desc: "The guide and the journal together — the full Cheat Code experience for men ready to do the work.",
    alt: "Promotional bundle showing both editions of The Cheat Code for Men side by side.",
  },
  {
    image: promoFathersDay,
    title: "Father's Day Special Bundle",
    desc: "A gift for the father-like guy in your life — The Cheat Code for Men, packaged for June.",
    alt: "Father's Day Special Bundle promotional image featuring The Cheat Code for Men.",
  },
  {
    image: promoSignature,
    title: "Dr. Royce Sullivan Signature Collection",
    desc: "A signature box including Built on Purpose, both Cheat Code editions, Sheep Bite, and Honest in the Dark.",
    alt: "Signature Collection box featuring multiple Dr. Sully titles displayed together.",
  },
  {
    image: promoTransformation,
    title: "The Transformation Collection",
    desc: "Dr. Sully's life-changing collection — from purpose and prayer to daily devotionals. Audible coming soon.",
    alt: "Transformation Collection promotional image showing the full Dr. Sully library.",
  },
];

const Books = () => {
  // Preload the top book covers so mobile browsers can start the LCP image
  // fetch during HTML parse. First cover gets high priority; next two get
  // low priority so we help paint without saturating the connection.
  const topCovers = BOOKS.slice(0, 3)
    .map((book, i) => {
      const pic = getBookPicture(book.image);
      return pic ? { pic, priority: i === 0 } : null;
    })
    .filter((v): v is { pic: NonNullable<ReturnType<typeof getBookPicture>>; priority: boolean } => !!v);
  const featuredSizes =
    "(min-width: 1024px) 22vw, (min-width: 640px) 45vw, 90vw";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Books by Dr. Sully — Faith, Leadership & Men's Growth"
        description="Shop paperback and eBook titles by Dr. RD 'Sully' Sullivan on leadership, purpose, church hurt healing, and men's growth. Print and digital editions."
        path="/books"
        ogType="book"
        jsonLd={buildBooksJsonLd(BOOKS)}
      />

      <Helmet>
        {/* Preconnect to the canonical image origin so the browser can warm the
            TLS handshake before the responsive <picture> sources resolve. */}
        <link rel="preconnect" href="https://drsullyspeaks.com" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="https://drsullyspeaks.com" />

        {topCovers.map(({ pic, priority }, i) => (
          <link
            key={`avif-${i}`}
            rel="preload"
            as="image"
            type="image/avif"
            imageSrcSet={pic.sources.avif}
            imageSizes={featuredSizes}
            fetchPriority={priority ? "high" : "low"}
          />
        ))}
        {topCovers.map(({ pic, priority }, i) => (
          <link
            key={`webp-${i}`}
            rel="preload"
            as="image"
            type="image/webp"
            imageSrcSet={pic.sources.webp}
            imageSizes={featuredSizes}
            fetchPriority={priority ? "high" : "low"}
          />
        ))}
      </Helmet>



      <SiteNav />

      <section className="relative pt-32 md:pt-40 pb-20 md:pb-24 bg-hero overflow-hidden">
        <div className="absolute inset-0 -z-0 opacity-[0.05] pointer-events-none select-none flex items-center justify-center">
          <span className="font-display font-bold uppercase text-white text-[16vw] tracking-[0.08em]">
            BOOKS
          </span>
        </div>

        <div className="container max-w-5xl text-center relative">
          <Reveal variant="up">
            <span className="eyebrow !mx-auto">
              <BookOpen className="inline h-3 w-3 mr-2" />
              Books & Resources
            </span>
            <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl lg:text-7xl mt-4 leading-[1.05]">
              Dr. Sully's
              <br />
              <span className="text-gradient-gold">bookshelf</span>
            </h1>
            <p className="text-silver text-base md:text-lg mt-6 max-w-3xl mx-auto leading-relaxed">
              A growing library of books, journals, magazines, and teaching tools
              rooted in leadership, healing, faith, identity, and honest growth.
            </p>
          </Reveal>

          <Reveal variant="up" delay={120}>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-px bg-border border border-border max-w-3xl mx-auto">
              <div className="bg-navy p-6">
                <div className="font-display text-3xl text-gradient-gold">{BOOKS.length}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-silver mt-2">Titles Featured</div>
              </div>
              <div className="bg-navy p-6">
                <div className="font-display text-3xl text-gradient-gold">{FORMATS.length}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-silver mt-2">Formats</div>
              </div>
              <div className="bg-navy p-6 col-span-2 md:col-span-1">
                <div className="font-display text-3xl text-gradient-gold">1</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-silver mt-2">Voice Across Every Title</div>
              </div>
            </div>
          </Reveal>

          <Reveal variant="up" delay={200}>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              {STOREFRONTS.map((store) => (
                <a
                  key={store.href}
                  href={store.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-7 py-5 shadow-cta hover:brightness-110 transition-all"
                >
                  <ShoppingBag className="h-4 w-4" />
                  Shop on {store.name.split(" / ")[0]}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
            <p className="text-silver/70 text-xs uppercase tracking-[0.25em] mt-4 text-center">
              Books sold via Books.by and Lulu Spotlight
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-28 bg-navy-deep border-y border-border">
        <div className="container max-w-6xl">
          <Reveal variant="up">
            <div className="text-center mb-14">
              <span className="eyebrow !mx-auto">
                <ShoppingBag className="inline h-3 w-3 mr-2" />
                Where to Buy
              </span>
              <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-4 leading-[1.05]">
                Two storefronts.
                <br />
                <span className="text-gradient-gold">One library.</span>
              </h2>
              <p className="text-silver text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
                Every Dr. Sully title is fulfilled through one of these two
                trusted storefronts. Pick the format you want, click through,
                and it ships direct.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {STOREFRONTS.map((store, i) => (
              <Reveal key={store.href} variant="up" delay={i * 100}>
                <a
                  href={store.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="premium-card group p-7 md:p-8 h-full flex flex-col justify-between hover:border-gold transition-all"
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">
                      Storefront
                    </div>
                    <h3 className="font-display uppercase text-white text-xl md:text-2xl leading-tight">
                      {store.name}
                    </h3>
                    <p className="text-silver text-sm mt-4 leading-relaxed">
                      {store.desc}
                    </p>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-white font-display uppercase tracking-[0.2em] text-xs border-b border-gold pb-2 self-start group-hover:text-gold transition-colors">
                    Visit storefront
                    <ExternalLink className="h-3.5 w-3.5" />
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      <section className="py-24 md:py-28">
        <div className="container max-w-6xl">
          <Reveal variant="up">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <span className="eyebrow">
                  <LibraryBig className="inline h-3 w-3 mr-2" />
                  Available Titles
                </span>
                <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-4 leading-[1.05]">
                  Written to help people
                  <br />
                  <span className="text-gradient-gold">name what matters.</span>
                </h2>
              </div>
              <p className="text-silver text-sm md:text-base max-w-xl leading-relaxed">
                Some titles are devotional, some corrective, some practical. All
                of them carry Dr. Sully's direct voice and purpose-centered lens.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
            {BOOKS.map((book, index) => (
              <Reveal key={book.title} variant="up" delay={index * 80}>
                <article
                  className={`premium-card overflow-hidden h-full flex flex-col relative ${
                    book.featured ? "ring-2 ring-gold shadow-[0_0_40px_-10px_hsl(var(--gold)/0.5)]" : ""
                  }`}
                >
                  {book.featured && (
                    <div className="absolute top-3 right-3 z-10 bg-gold text-navy-deep font-display uppercase tracking-[0.2em] text-[10px] px-3 py-1.5 rounded-sm shadow-lg">
                      New Release
                    </div>
                  )}
                  <div className="aspect-[4/5] overflow-hidden border-b border-border bg-navy-deep">
                    <BookCoverImage
                      src={book.image}
                      alt={book.alt}
                      className="h-full w-full object-cover"
                      priority={index < 3}
                    />
                  </div>


                  <div className="p-6 md:p-7 flex flex-col flex-1">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">
                      {book.format}
                    </div>
                    <h3 className="font-display uppercase text-white text-2xl leading-tight">
                      {book.title}
                    </h3>
                    <p className="text-orange text-sm uppercase tracking-[0.14em] mt-3 leading-relaxed">
                      {book.subtitle}
                    </p>
                    <p className="text-silver text-sm mt-5 leading-relaxed flex-1">
                      {book.desc}
                    </p>

                    {book.buyLinks && book.buyLinks.length > 0 ? (
                      <div className="mt-6 flex flex-col gap-2">
                        {book.buyLinks.map((link) => (
                          <a
                            key={link.href}
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              trackCTA("book_buy_click", {
                                book: book.title,
                                format: link.label,
                                surface: "books_page_card",
                              })
                            }
                            className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-5 py-3 shadow-cta hover:brightness-110 transition-all"
                          >
                            <ShoppingBag className="h-3.5 w-3.5" />
                            {link.label}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ))}
                        <a
                          href={`mailto:${EMAIL}?subject=${encodeURIComponent(`Ask about ${book.title}`)}`}
                          className="inline-flex items-center justify-center gap-2 text-silver hover:text-white font-display uppercase tracking-[0.2em] text-[11px] pt-2 transition-colors"
                        >
                          Ask about this title
                          <ArrowRight className="h-3 w-3" />
                        </a>
                      </div>
                    ) : (
                      <a
                        href={`mailto:${EMAIL}?subject=${encodeURIComponent(`Ask about ${book.title}`)}`}
                        className="mt-6 inline-flex items-center gap-2 text-white font-display uppercase tracking-[0.2em] text-xs border-b border-gold pb-2 hover:text-gold transition-colors self-start"
                      >
                        Ask about this title
                        <ArrowRight className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-28">
        <div className="container max-w-6xl">
          <Reveal variant="up">
            <div className="text-center mb-14">
              <span className="eyebrow !mx-auto">
                <LibraryBig className="inline h-3 w-3 mr-2" />
                Bundles & Collections
              </span>
              <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-4 leading-[1.05]">
                Featured bundles &
                <br />
                <span className="text-gradient-gold">signature collections.</span>
              </h2>
              <p className="text-silver text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
                Curated bundles built around themes — men, fathers, leaders, and
                the full transformation library.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-8">
            {PROMOS.map((promo, i) => (
              <Reveal key={promo.title} variant="up" delay={i * 80}>
                <article className="premium-card overflow-hidden h-full flex flex-col">
                  <div className="aspect-[3/4] md:aspect-[4/5] overflow-hidden border-b border-border bg-navy-deep">
                    <BookCoverImage
                      src={promo.image}
                      alt={promo.alt}
                      className="h-full w-full object-cover"
                      sizes="(min-width: 768px) 45vw, 92vw"
                    />
                  </div>

                  <div className="p-6 md:p-7 flex flex-col flex-1">
                    <h3 className="font-display uppercase text-white text-xl md:text-2xl leading-tight">
                      {promo.title}
                    </h3>
                    <p className="text-silver text-sm mt-4 leading-relaxed flex-1">
                      {promo.desc}
                    </p>
                    <a
                      href="https://www.lulu.com/spotlight/drsullyspeaks"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 inline-flex items-center gap-2 text-white font-display uppercase tracking-[0.2em] text-xs border-b border-gold pb-2 hover:text-gold transition-colors self-start"
                    >
                      Shop this bundle
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-navy-elevated/40 border-y border-border">
        <div className="container max-w-4xl text-center">
          <Reveal variant="up">
            <span className="eyebrow !mx-auto">
              <LibraryBig className="inline h-3 w-3 mr-2" />
              The full catalog
            </span>
            <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-4 leading-[1.05]">
              Shop all Dr. Sully titles<br />
              <span className="text-gradient-gold">on Lulu.</span>
            </h2>
            <p className="text-silver text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
              Every ebook and paperback published by Dr. Sully — sorted by best-sellers,
              updated as new titles release. Buy directly from the author on Lulu.
            </p>
            <div className="mt-8 flex justify-center">
              <a
                href="https://www.lulu.com/search?contributor=Dr.+RD+%E2%80%9CSully%E2%80%9D+Sullivan&sortBy=PRODUCT_SALES_90_DAYS"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackCTA("book_shop_all_click", { surface: "books_catalog", destination: "lulu_contributor" })
                }
                className="group inline-flex items-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-7 py-5 shadow-cta hover:brightness-110 active:scale-[0.98] transition-all"
              >
                <ShoppingBag className="h-4 w-4" />
                Shop All Titles on Lulu
                <ExternalLink className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-navy-deep border-y border-border">


        <div className="container max-w-4xl text-center">
          <Reveal variant="up">
            <span className="eyebrow !mx-auto">
              <Mail className="inline h-3 w-3 mr-2" />
              Need a title for your audience?
            </span>
            <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-4 leading-[1.05]">
              Ask about book features,
              <br />
              <span className="text-gradient-gold">bulk orders, or bundles.</span>
            </h2>
            <p className="text-silver text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
              If you want to pair a title with a keynote, a workshop, a men&apos;s
              event, or a church leadership experience, start the conversation here.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/inquire"
                className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-7 py-5 shadow-cta hover:brightness-110 transition-all"
              >
                Inquire About Books
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`mailto:${EMAIL}?subject=Book%20Inquiry`}
                className="inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-xs px-7 py-5 hover:bg-gold/10 transition-all"
              >
                Email Dr. Sully
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Cross-link to /ebooks — sibling money page for the digital shopper.
          Google treats prominent contextual anchors like these as strong
          topical relationships between the two hub pages. */}
      <section aria-label="Also explore" className="py-16 md:py-20 border-t border-border bg-navy-deep">
        <div className="container max-w-3xl text-center">
          <p className="eyebrow !mx-auto">Prefer instant PDF?</p>
          <h2 className="font-display uppercase text-white text-2xl md:text-3xl mt-4 leading-tight">
            Every workbook, playbook &amp; devotional —{" "}
            <Link to="/ebooks" className="text-gold underline decoration-gold/40 hover:text-gold-light">
              browse the eBook library →
            </Link>
          </h2>
          <p className="text-silver text-sm md:text-base mt-4 max-w-xl mx-auto leading-relaxed">
            Instant download, no shipping wait. Read on phone, tablet, or desktop.
          </p>
        </div>
      </section>


      <SiteFooter />
    </div>
  );
};

export default Books;
